<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: undefined,
  },
  visible: {
    type: Boolean,
    required: undefined,
  },
  title: {
    type: String,
    default: '',
  },
  dialogClass: {
    type: String,
    default: '',
  },
  isCloseButton: {
    type: Boolean,
    default: true,
  },
  dialogStyle: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const closeDialog = () => {
  emit('update:modelValue', false);
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeDialog();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

const isVisible = computed(() => {
  return props.modelValue !== undefined ? props.modelValue : props.visible;
});
</script>
<template>
  <div v-if="isVisible" class="dialog">
    <transition name="overlay-fade">
      <div v-if="isVisible" class="dialog__overlay" @click="closeDialog"></div>
    </transition>
    <transition name="dialog-modal" appear>
      <div v-if="isVisible" class="dialog__content" :class="dialogClass" :style="dialogStyle">
        <div class="flex flex-row justify-between">
          <h1 class="font-normal text-lg">
            {{ title }}
          </h1>
          <!-- Close Button -->
          <button v-if="isCloseButton" @click="closeDialog">
            <img src="@/assets/images/exit.svg" width="25" class="mr-2" />
          </button>
        </div>
        <div class="mt-3 overflow-auto">
          <slot></slot>
        </div>
        <div class="dialog__footer flex justify-end mt-4" :class="dialogClass" :style="dialogStyle">
          <slot name="footer"></slot>
        </div>
      </div>
    </transition>
  </div>
</template>
<style>
.dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  font-size: 14px;
}

.dialog__overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.dialog__content {
  position: relative;
  z-index: 1000;
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  margin: auto;
}
.dialog__footer {
  margin-top: 20px;
  height: fit-content !important;
  width: 100% !important;
}

/* dialog fade and scale animation */
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}
.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
.dialog-fade-enter-to,
.dialog-fade-leave-from {
  opacity: 1;
  transform: scale(1);
}

/* overlay fade animation */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.2s ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}
.overlay-fade-enter-to,
.overlay-fade-leave-from {
  opacity: 1;
}

/* dialog content slide & fade animation */
.dialog-modal-enter-active,
.dialog-modal-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}
.dialog-modal-enter-from {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
.dialog-modal-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.dialog-modal-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.dialog-modal-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
</style>
